/**
 * The bitrate ladder, wired to a live uploader status.
 *
 * Kept apart from the policy in `adaptiveBitrate.ts` so the rules stay
 * testable without a MediaRecorder. All this adds is a clock and somewhere to
 * hold the state between readings.
 *
 * The returned value is passed straight to `useRecorder`, where it is a
 * dependency of the recorder-build effect — so a step down rebuilds the
 * recorder, which starts a new session. That is safe (chunks carry their
 * session, and the player splits runs on it) and the in-flight blob still
 * lands in IndexedDB for the next uploader to collect, but it is not free,
 * which is why the policy insists on a full minute of sustained backlog first.
 */
import { useEffect, useRef, useState } from "react";
import {
  NO_BACKLOG,
  nextBitrate,
  type AdaptiveSource,
  type BacklogState,
} from "./adaptiveBitrate";

/** How often the backlog is sampled. Well below the one-minute window. */
const SAMPLE_MS = 5_000;

export function useAdaptiveBitrate({
  source,
  pending,
  enabled = true,
}: {
  source: AdaptiveSource;
  pending: number;
  enabled?: boolean;
}): number {
  const [bitrate, setBitrate] = useState<number>(
    () => nextBitrate({ pending: 0, nowMs: Date.now(), state: NO_BACKLOG, source }).bitrate,
  );
  const state = useRef<BacklogState>(NO_BACKLOG);

  // Read through a ref so the sampling interval is not rebuilt every time the
  // queue length changes — which is constantly.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  useEffect(() => {
    if (!enabled) return;

    const sample = () => {
      const decision = nextBitrate({
        pending: pendingRef.current,
        nowMs: Date.now(),
        state: state.current,
        source,
      });
      state.current = decision.state;
      if (decision.steppedDown) {
        console.warn(
          `[recorder] ${source}: upload cannot keep up (${pendingRef.current} queued) — dropping to ${decision.bitrate} bps`,
        );
        setBitrate(decision.bitrate);
      }
    };

    const id = setInterval(sample, SAMPLE_MS);
    return () => clearInterval(id);
  }, [source, enabled]);

  return bitrate;
}
